import React from "react";
import { Card, Row, Col, Descriptions, Button, Image, Divider } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { formatDisplayDate } from "../../../../utils/DateAndTimeConvertor";

interface KycDetailsProps {
  kycData: any;
  onEdit: () => void;
  onResubmit: () => void;
  loading?: boolean;
}

const KycDetails: React.FC<KycDetailsProps> = ({
  kycData,
  onResubmit,
  loading = false,
}) => {
  if (!kycData) return null;

  const statusColor: Record<string, string> = {
    approved: "#52c41a",
    pending: "#faad14",
    rejected: "#f5222d",
    in_review: "#1890ff",
  };

  const renderImage = (fileArray: any[]) => {
    if (!fileArray || !Array.isArray(fileArray) || fileArray.length === 0) {
      return <span className="text-gray-500">No file uploaded</span>;
    }
    return (
      <Image
        src={fileArray[0]?.url}
        alt="uploaded file"
        style={{ maxWidth: "200px", maxHeight: "200px" }}
        preview
      />
    );
  };

  return (
    <div className="h-full mb-10">
      <Card
        title="KYC & Bank Details"
        bordered={false}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={onResubmit}
            loading={loading}
          >
            Re-KYC
          </Button>
        }
      >
        {/* Status Badge */}
        <div className="my-6 flex justify-between items-center">
          <span
            style={{
              backgroundColor: statusColor[kycData?.status] || "#faad14",
              color: "white",
              padding: "4px 12px",
              borderRadius: "4px",
              textTransform: "capitalize",
              fontWeight: "500",
            }}
          >
            Status: {kycData?.status}
          </span>
          <div>
            {kycData?.approved_at && (
              <div className="text-gray-600 mt-2">
                Approved on:{" "}
                {formatDisplayDate(kycData.approved_at)}
              </div>
            )}
            {kycData?.admin_remark && (
              <div className="text-gray-600 mt-1">
                <strong>Admin comment:</strong> {kycData.admin_remark}
              </div>
            )}
          </div>
        </div>

        <Divider />

        {/* PAN Details */}
        <h3 className="text-lg font-semibold mb-4">PAN Details</h3>
        <Row gutter={16} className="mb-6">
          <Col xs={24} md={12}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Name on PAN">
                {kycData?.pan_name}
              </Descriptions.Item>
              <Descriptions.Item label="PAN Number">
                {kycData?.pan_number}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <div>
              <p className="font-semibold mb-2">PAN Card:</p>
              {renderImage(kycData?.pancard_id as any)}
            </div>
          </Col>
        </Row>

        <Divider />

        {/* Bank Details */}
        <h3 className="text-lg font-semibold mb-4">Bank Details</h3>
        <Row gutter={16} className="mb-6">
          <Col xs={24} md={12}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Account Holder Name">
                {kycData?.account_holder_name}
              </Descriptions.Item>
              <Descriptions.Item label="Bank Name">
                {kycData?.bank_name}
              </Descriptions.Item>
              <Descriptions.Item label="Branch Name">
                {kycData?.branch_name}
              </Descriptions.Item>
              <Descriptions.Item label="Account Type">
                {kycData?.account_type}
              </Descriptions.Item>
              <Descriptions.Item label="IFSC Code">
                {kycData?.ifsc_code}
              </Descriptions.Item>
              <Descriptions.Item label="Account Number">
                {"*".repeat(
                  Math.max(0, (kycData?.account_number?.length ?? 4) - 4),
                ) + kycData?.account_number?.slice(-4)}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <div>
              <p className="font-semibold mb-2">Blank Cheque:</p>
              {renderImage(kycData?.bank_cheque_id as any)}
            </div>
          </Col>
        </Row>

        {/* Optional GSTIN */}
        {kycData?.gstin && (
          <>
            <Divider />
            <h3 className="text-lg font-semibold mb-4">GST Details</h3>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="GSTIN">
                {kycData?.gstin}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Card>
    </div>
  );
};

export default KycDetails;
